import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import AnalysisStatusIndicator from '../components/AnalysisStatusIndicator';

interface AnalysisState {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'error';
  progress: number;
  message: string;
  unread?: boolean; // show badge in navbar when completed
}

interface AnalysisContextType {
  activeAnalyses: AnalysisState[];
  addAnalysis: (analysisId: string) => void;
  removeAnalysis: (analysisId: string) => void;
  updateAnalysis: (analysisId: string, updates: Partial<AnalysisState>) => void;
  markAllRead: () => void;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

export const useAnalysisContext = () => {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysisContext must be used within an AnalysisProvider');
  }
  return context;
};

interface AnalysisProviderProps {
  children: ReactNode;
}

export const AnalysisProvider: React.FC<AnalysisProviderProps> = ({ children }) => {
  const [activeAnalyses, setActiveAnalyses] = useState<AnalysisState[]>([]);

  const addAnalysis = useCallback((analysisId: string) => {
    setActiveAnalyses(prev => {
      // Don't add if already exists
      if (prev.some(analysis => analysis.id === analysisId)) {
        return prev;
      }
      
      return [...prev, {
        id: analysisId,
        status: 'queued',
        progress: 0,
        message: 'Starting analysis...'
      }];
    });
  }, []);

  const removeAnalysis = useCallback((analysisId: string) => {
    setActiveAnalyses(prev => prev.filter(analysis => analysis.id !== analysisId));
  }, []);

  const updateAnalysis = useCallback((analysisId: string, updates: Partial<AnalysisState>) => {
    setActiveAnalyses(prev => 
      prev.map(analysis => 
        analysis.id === analysisId 
          ? { ...analysis, ...updates }
          : analysis
      )
    );
  }, []);

  const markAllRead = useCallback(() => {
    setActiveAnalyses(prev => prev.map(a => ({ ...a, unread: false })));
  }, []);

  const handleViewReport = useCallback((analysisId: string) => {
    window.location.href = `/report/${analysisId}`;
  }, []);

  const handleDismiss = useCallback((analysisId: string) => {
    removeAnalysis(analysisId);
  }, [removeAnalysis]);

  return (
    <AnalysisContext.Provider value={{
      activeAnalyses,
      addAnalysis,
      removeAnalysis,
      updateAnalysis,
      markAllRead
    }}>
      {children}
      
      {/* Render floating indicators for active analyses */}
      {activeAnalyses.map(analysis => (
        <AnalysisStatusIndicator
          key={analysis.id}
          analysisId={analysis.id}
          onViewReport={() => handleViewReport(analysis.id)}
          onDismiss={() => handleDismiss(analysis.id)}
        />
      ))}
    </AnalysisContext.Provider>
  );
};
